import * as turf from '@turf/turf'

//APP
import type { GeoPointFeature } from '../../../../../../../types'
// Tipo reaproveitado do modal de criação (pasta irmã `criarMalha`): o painel de
// configurações é compartilhado e tipa as próprias props com ele, então declarar
// um tipo paralelo aqui quebraria a compilação.
import type { GridMalha } from '../../criarMalha/components/gerarMalha'

/** Máximo de pontos usados para estimar lado e orientação — o resto só é convertido. */
const AMOSTRA_ESTIMATIVA = 200

/** Vizinho a menos disso (em metros) é ponto duplicado: não serve de referência. */
const DISTANCIA_MINIMA_VIZINHO = 0.5

/**
 * Abaixo deste comprimento resultante a média circular dos rumos não descreve um
 * reticulado — a malha não tem orientação dominante e os quadrados vão sem giro.
 */
const CONFIANCA_MINIMA_ANGULO = 0.3

/**
 * Rumos dos quatro cantos do quadrado, somados ao giro do reticulado. A ordem é
 * anti-horária (NE, NO, SO, SE), como nas células do grid gerado pelo `turf.squareGrid`.
 */
const CANTOS_DO_QUADRADO = [45, 315, 225, 135]

type PontoNormalizado = {
  lat: number
  lng: number
  /** Numeração salva da malha; ausente quando o dado não trouxe um número usável. */
  posicao?: number
}

type VizinhoMaisProximo = {
  distancia: number
  rumo: number
}

export interface MalhaReconstruida {
  grid: GridMalha[]
  /** Lado da célula em metros — estimado a partir do espaçamento da própria malha. */
  ladoMetros: number
  /** Giro do reticulado em graus, em [0, 90). */
  anguloGraus: number
  areaCelulaM2: number
  /** Pontos dentro do contorno — é o que o modal usa como `tamanhoGridOriginal`. */
  totalDentroDoContorno: number
  /** Pontos fora do contorno: nascem ocultos e são descartados ao salvar. */
  foraDoContorno: number
}

/**
 * `LATITUDE`/`LONGITUDE` chegam como **string** quando a OS foi hidratada do
 * backend, e `NOME` é o `parseInt` de um campo livre — pode ser `NaN`. Deixar um
 * `NaN` passar aqui é pior que descartar o ponto: `NaN` não é capturado pelo `??`
 * do `salvarMalha` e a malha seria gravada com `INDICE: NaN`.
 */
function normalizarPontos(pontos: GeoPointFeature[]): PontoNormalizado[] {
  return (pontos ?? []).reduce<PontoNormalizado[]>((acc, ponto) => {
    const lat = Number(ponto?.LATITUDE)
    const lng = Number(ponto?.LONGITUDE)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return acc

    const numero = Number(ponto?.NOME)
    const posicao = Number.isFinite(numero) && numero > 0 ? numero : undefined

    acc.push({ lat, lng, posicao })
    return acc
  }, [])
}

function mediana(valores: number[]): number | null {
  if (valores.length === 0) return null

  const ordenados = [...valores].sort((a, b) => a - b)
  const meio = Math.floor(ordenados.length / 2)

  return ordenados.length % 2 === 0 ? (ordenados[meio - 1] + ordenados[meio]) / 2 : ordenados[meio]
}

/**
 * Distância e rumo até o vizinho mais próximo de cada ponto da amostra.
 *
 * A amostra é o prefixo da lista (por isso os índices batem com `pontos`): a
 * varredura é O(amostra × total) e serve só para estimar a geometria do
 * reticulado, não para desenhar nada.
 */
function vizinhosMaisProximos(pontos: PontoNormalizado[]): VizinhoMaisProximo[] {
  const vizinhos: VizinhoMaisProximo[] = []
  const limite = Math.min(pontos.length, AMOSTRA_ESTIMATIVA)

  for (let i = 0; i < limite; i++) {
    const origem = turf.point([pontos[i].lng, pontos[i].lat])
    let maisProximo: VizinhoMaisProximo | null = null

    for (let j = 0; j < pontos.length; j++) {
      if (i === j) continue

      const destino = turf.point([pontos[j].lng, pontos[j].lat])
      const distancia = turf.distance(origem, destino, { units: 'meters' })

      if (distancia < DISTANCIA_MINIMA_VIZINHO) continue
      if (maisProximo && distancia >= maisProximo.distancia) continue

      maisProximo = { distancia, rumo: turf.bearing(origem, destino) }
    }

    if (maisProximo) vizinhos.push(maisProximo)
  }

  return vizinhos
}

function estimarLado(
  vizinhos: VizinhoMaisProximo[],
  totalPontos: number,
  areaHa: number
): number | null {
  const espacamento = mediana(vizinhos.map(({ distancia }) => distancia))
  if (espacamento && espacamento > 0) return espacamento

  // Malha de um único ponto (ou de pontos todos duplicados): sem vizinho para
  // medir, o lado sai da área por ponto — a mesma conta do `gerarGrid`.
  const lado = Math.sqrt((areaHa * 10000) / totalPontos)
  return Number.isFinite(lado) && lado > 0 ? lado : null
}

/**
 * Giro do reticulado, em [0, 90).
 *
 * Num reticulado quadrado os vizinhos ficam a θ, θ+90, θ+180 e θ+270, então
 * reduzir o rumo a [0, 90) faz os quatro colapsarem em θ. A média é circular
 * (o ângulo é multiplicado por 4 para ocupar a volta inteira): uma média
 * aritmética partiria um θ próximo de zero entre ~0° e ~90° e devolveria ~45°.
 */
function orientacaoDaMalha(vizinhos: VizinhoMaisProximo[]): number {
  if (vizinhos.length === 0) return 0

  let somaSeno = 0
  let somaCosseno = 0

  vizinhos.forEach(({ rumo }) => {
    const reduzido = ((rumo % 90) + 90) % 90
    const radianos = (reduzido * 4 * Math.PI) / 180
    somaSeno += Math.sin(radianos)
    somaCosseno += Math.cos(radianos)
  })

  const confianca = Math.hypot(somaSeno, somaCosseno) / vizinhos.length
  if (confianca < CONFIANCA_MINIMA_ANGULO) return 0

  const media = (Math.atan2(somaSeno, somaCosseno) * 180) / Math.PI / 4
  return ((media % 90) + 90) % 90
}

/** Quadrado de lado `ladoMetros` centrado no ponto e girado por `anguloGraus`. */
function quadradoAoRedor(
  centro: PontoNormalizado,
  ladoMetros: number,
  anguloGraus: number
): turf.Feature<turf.Polygon> {
  const centroTurf = turf.point([centro.lng, centro.lat])
  const meiaDiagonal = (ladoMetros / 2) * Math.SQRT2

  const anel = CANTOS_DO_QUADRADO.map(
    (canto) =>
      turf.destination(centroTurf, meiaDiagonal, anguloGraus + canto, { units: 'meters' })
        .geometry.coordinates
  )

  anel.push([...anel[0]])

  return turf.polygon([anel])
}

/**
 * Reconstrói as células de grid que o mapa precisa a partir dos pontos de uma
 * malha já salva.
 *
 * O modal não trabalha com pontos: trabalha com células (`GridMalha`), e é o
 * polígono da célula que alimenta a rotação, o desenho dos quadrados e a
 * ordenação zig-zag. A malha salva guarda só os pontos, então aqui cada ponto
 * ganha uma célula própria — dimensionada e girada conforme o espaçamento medido
 * na própria malha, para os quadrados encaixarem como no grid que os gerou.
 *
 * Devolve `null` quando não há ponto válido, contorno ou lado estimável: sem
 * qualquer um dos três não existe malha para editar.
 */
export function malhaParaGrid(
  pontosSalvos: GeoPointFeature[],
  contorno: turf.Feature<turf.Polygon> | null,
  areaHa: number
): MalhaReconstruida | null {
  const pontos = normalizarPontos(pontosSalvos)
  if (pontos.length === 0 || !contorno) return null

  const vizinhos = vizinhosMaisProximos(pontos)
  const ladoMetros = estimarLado(vizinhos, pontos.length, areaHa)
  if (!ladoMetros) return null

  const anguloGraus = orientacaoDaMalha(vizinhos)

  let foraDoContorno = 0

  const grid = pontos.map((ponto, index): GridMalha => {
    const dentro = turf.booleanPointInPolygon(turf.point([ponto.lng, ponto.lat]), contorno)
    if (!dentro) foraDoContorno += 1

    return {
      id: index + 1,
      idPolygon: index + 2,
      // O mapa reescreve `oculto` comparando o ponto com o contorno; nascer já com
      // o resultado dessa mesma conta evita os pontos de fora piscarem no primeiro
      // render.
      oculto: !dentro,
      posicao: ponto.posicao,
      center: [ponto.lat, ponto.lng],
      // Centrado NO PONTO SALVO, e não num reticulado ideal: o mapa rederiva o
      // `center` de `turf.centerOfMass(polygons)` a cada rotação, então é isso que
      // mantém o ponto exatamente onde ele foi salvo.
      polygons: quadradoAoRedor(ponto, ladoMetros, anguloGraus),
    }
  })

  return {
    grid,
    ladoMetros,
    anguloGraus,
    areaCelulaM2: ladoMetros * ladoMetros,
    totalDentroDoContorno: pontos.length - foraDoContorno,
    foraDoContorno,
  }
}
